// FAR-54: which folder the Plans screen starts on.
//
// The rule is the folder with the most recent Created timestamp -- not the
// first alphabetically, and not the last one viewed. A planner's newest folder
// is the one they are working in.

// newestFolderId(folders) -> the id of the folder created most recently, or ''
// when there are none.
//
// Ties break on id, so the choice is stable rather than depending on whatever
// order DynamoDB returned. Folders with no timestamp sort last: a folder
// stored before FAR-49 has no Created stamp and must not win by accident.
export function newestFolderId(folders) {
  const list = (folders || []).filter((f) => f && f.id)
  if (list.length === 0) return ''

  let best = null
  for (const f of list) {
    if (best === null || isNewer(f, best)) best = f
  }
  return best.id
}

function time(folder) {
  const t = Date.parse(folder?.createdAt ?? '')
  return Number.isNaN(t) ? null : t
}

function isNewer(candidate, best) {
  const a = time(candidate)
  const b = time(best)
  // Undated folders (stored before FAR-49) sort last. Comparing null with a
  // number is false either way, so an undated candidate never wins and an
  // undated best is always beaten -- no explicit guard needed.
  if (a === null && b === null) return candidate.id < best.id
  if (a !== b) return a > b
  return candidate.id < best.id
}

// keepOrSelectNewest decides the selected folder when the folder list loads or
// changes: keep the planner's current choice if it still exists, else fall
// back to the newest. Without the first half, re-fetching the list would yank
// a planner back to the newest folder mid-task.
export function keepOrSelectNewest(folders, currentId) {
  if (currentId && (folders || []).some((f) => f?.id === currentId)) return currentId
  return newestFolderId(folders)
}

// FAR-70: folder dropdowns list the newest folder first, the same order the
// Base Plan picker uses. Undated folders (pre-FAR-49) go last; equal or
// missing timestamps order by id. A total order spelled out step by step, so
// it doesn't depend on the sort algorithm. Doesn't touch the input.
export function foldersNewestFirst(folders) {
  return [...(folders || [])].filter((f) => f && f.id).sort(compareNewestFirst)
}

function compareNewestFirst(a, b) {
  const ta = time(a)
  const tb = time(b)
  if (ta !== tb) {
    if (ta === null) return 1 // undated after dated
    if (tb === null) return -1
    return tb - ta // newer first
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0 // 0 only for the same id
}
